import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, forwardRef, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { cloneDeep } from 'lodash';
import { Action, ActionBehavior, areActionsEqual } from '../../../utils/action-utils';
import { SelectableOption } from '../../../utils/form-utils';
import { buttonTypeSelectOptions } from '../../../utils/shared/button/button-type-select-options';
import { PopConfirmPlacement } from '../../../utils/button/pop-confirm-placement.enum';
import { IconComponentConfig } from '../../../utils/icon';
import { IconType } from '../../../utils/icon/icon-type.enum';

@Component({
  selector: 'rappider-actions-input',
  templateUrl: './actions-input.component.html',
  styleUrls: ['./actions-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => ActionsInputComponent),
      multi: true
    }
  ]
})
export class ActionsInputComponent implements OnInit {
  @Input() hideDragIcon = false;
  @Input() showVisibilityToggle = false;
  @Input() customDeleteAction = false;
  @Input() hideAddButton = false;
  @Input() hideDeleteButton = false;
  @Output() visibilityToggle = new EventEmitter<{ index: number; visible: boolean }>();
  @Output() customDelete = new EventEmitter<number>();

  _value: Action[];
  typeSelectOptions: SelectableOption[] = Object.entries(ActionBehavior).map(([key, value]) => ({ key: key, value: value }));
  buttonTypeSelectableOption: SelectableOption[] = Object.entries(ActionBehavior).map(([key, value]) => ({ key: key, value: value }));
  popConfirmPlacementTypeSelectableOption: SelectableOption[] = Object.entries(PopConfirmPlacement).map(([key, value]) => ({ key: key, value: value }));
  initialAction: any = {
    type: undefined,
    title: undefined,
    fieldName: undefined,
    emitWithoutPopconfirm: true
  };


  editedIndex: number | undefined;
  isFirstInitialize = true;
  tempActionItem: Action = cloneDeep(this.initialAction);

  editButtonIconConfig: IconComponentConfig = {
    type: IconType.FontAwesome,
    name: 'fa-light fa-pen-to-square'
  };

  deleteButtonIconConfig: IconComponentConfig = {
    type: IconType.FontAwesome,
    name: 'fa-sharp fa-solid fa-trash'
  };

  visibilityOnIconConfig: IconComponentConfig = {
    type: IconType.FontAwesome,
    name: 'fa-regular fa-eye'
  };

  visibilityOffIconConfig: IconComponentConfig = {
    type: IconType.FontAwesome,
    name: 'fa-regular fa-eye-slash'
  };

  collapseIconConfig: IconComponentConfig = {
    type: IconType.FontAwesome,
    name: 'fa-light fa-chevron-up'
  };

  /* form submittion state */
  tempValueSubmitted = false;
  /* has error while adding new item */
  hasAddError = false;
  /* if the item is new, its true else ( edits ) it's false */
  isAddingAction = false;
  /* populated example data from field type */
  populatedData: Record<string, unknown>[] = [];
  /* select component options for button type */
  buttonTypeSelectOptions = buttonTypeSelectOptions;

  /* select component options for behavior */
  behaviorSelectOptions = Object.entries(ActionBehavior).map(([key, value]) => ({ key: key, value: value }));

  ActionBehavior = ActionBehavior;
  get value() {
    return this._value;
  }

  set value(value: Action[]) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
  }

  constructor() { }

  ngOnInit(): void {
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  onInputChange() {
  }

  onInputBlur() {
    this.onChange(this.value);
  }

  writeValue(value: Action[]) {
    if (!value) {
      if (!this._value || this._value.length !== 0) {
        this._value = [];
        this.editedIndex = undefined;
        this.isFirstInitialize = true;
      }
    } else {
      const editingStates = this._value?.map((item, index) => ({
        index,
        isEditing: item.isEditing
      })) || [];

      const newValue = value.map((item, index) => {
        const editingState = editingStates.find(es => es.index === index);
        if (editingState && editingState.isEditing) {
          return { ...item, isEditing: true };
        }
        return item;
      });

      if (this._value !== newValue && (!this._value || this._value.length !== newValue.length ||
          !areActionsEqual(this._value, newValue))) {
        this._value = newValue;
        this.editedIndex = undefined;
        this.isFirstInitialize = false;
      } else if (this._value && this._value.length === newValue.length) {
        for (let i = 0; i < this._value.length; i++) {
          if (this._value[i].isEditing && !newValue[i].isEditing) {
            this._value[i] = { ...newValue[i], isEditing: true };
          } else if (!this._value[i].isEditing && newValue[i].isEditing) {
            this._value[i] = { ...newValue[i] };
          }
        }
      }
    }
  }


  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  /**
   * Add a new action to the list
   */
  addAction() {
    if (!this.value) {
      this.value = [];
    }

    this.value.forEach(item => {
      item.isEditing = false;
    });

    const newAction = cloneDeep(this.initialAction);
    newAction['isEditing'] = true;
    this.value.push(newAction);

    this.hasAddError = false;

    this.onChange(this.value);
  }

  /**
   * This method is kept for backward compatibility
   * Now changes are saved automatically via onInputChange
   */
  onSave() {
    this.value[this.editedIndex] = this.tempActionItem;
    this.clearTempValue();
    this.isFirstInitialize = false;
    this.tempValueSubmitted = false;
    this.isAddingAction = false;
    this.onChange(this.value);
  }

  onEditItemClick(index: number) {
    if (this.value) {
      this.value.forEach(item => {
        item.isEditing = false;
      });
    }
    this.value[index].isEditing = true;
    this.onChange(this.value);
  }

  onEditCancel() {
    if (this.value && this.value.length > 0) {
      this.value.forEach(item => {
        if (item.isEditing) {
          item.isEditing = false;
        }
      });
      this.onChange(this.value);
    }
  }

  clearTempValue() {
    this.tempActionItem = cloneDeep(this.initialAction);
    this.editedIndex = undefined;
  }

  onDeleteItemClick(deletedItemIndex: number) {
    if (this.customDeleteAction) {
      this.customDelete.emit(deletedItemIndex);
    } else {
      this.value = this.value.filter((item, index) => index !== deletedItemIndex);
    }
  }

  /**
   * Toggle visibility of an action item
   * @param index Index of the action item
   */
  onVisibilityToggleClick(index: number) {
    if (!this.value || !this.value[index]) {
      return;
    }
    const currentVisible = (this.value[index] as any).visible !== false;
    (this.value[index] as any).visible = !currentVisible;
    this.onChange(this.value);
    this.visibilityToggle.emit({ index, visible: !currentVisible });
  }

  /**
   * Check if action item is visible
   * @param action Action item to check
   * @returns true if visible, false otherwise
   */
  isActionVisible(action: Action): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (action as any).visible !== false;
  }

  /**
   * Handle visibility toggle click on empty box
   * Creates a new action with visible: true
   */
  onEmptyBoxVisibilityClick() {
    this.addAction();
    // After adding, set the new action as visible
    if (this.value && this.value.length > 0) {
      const newAction = this.value[this.value.length - 1];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (newAction as any).visible = true;
      this.onChange(this.value);
      this.visibilityToggle.emit({ index: this.value.length - 1, visible: true });
    }
  }

  /**
   * Handle collapse click - closes edit mode and returns to box view
   * @param index Index of the action item to collapse
   */
  onCollapseClick(index: number) {
    if (!this.value || !this.value[index]) {
      return;
    }
    this.value[index].isEditing = false;
    this.onChange(this.value);
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.value, event.previousIndex, event.currentIndex);
    this.onChange(this.value);
  }

  /**
   * Show validation errors for required fields
   */
  showValidationErrors() {
    this.tempValueSubmitted = true;
  }

  resetValidationErrors() {
    this.tempValueSubmitted = false;
  }

}
